import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { ClaimsService } from '@core/authentication/claims.service';
import { AssignableUsersService } from './assignable-users.service';
import { UserList } from './user';

export type UserProfile = {
  roleName: string;
};

/**
 * Service for user-related utility methods.
 * Delegates assignable users loading to AssignableUsersService.
 */
@Injectable({
  providedIn: 'root'
})
export class UserService {
  constructor(
    private claimsService: ClaimsService,
    private assignableUsersService: AssignableUsersService
  ) {}

  /**
   * Get assignable users. Delegates to AssignableUsersService.
   * All subscribers share cached data - only one HTTP request per session.
   * 
   * @param startWithEmpty If true, immediately emits an empty list before cached data
   * @param throwError If true, errors will propagate; if false, returns empty list on error
   */
  getAssignableUsers(
    startWithEmpty: boolean = false,
    throwError: boolean = false
  ): Observable<UserList> {
    return this.assignableUsersService.getAssignableUsers(startWithEmpty, throwError);
  }

  /**
   * Get assignable users of every activation status (active and inactive).
   * For read-only filtering only (e.g. the Opportunities "Owned by" filter),
   * never for assigning work. Delegates to AssignableUsersService.
   *
   * @param startWithEmpty If true, immediately emits an empty list before cached data
   * @param throwError If true, errors will propagate; if false, returns empty list on error
   */
  getAssignableUsersIncludingInactive(
    startWithEmpty: boolean = false,
    throwError: boolean = false
  ): Observable<UserList> {
    return this.assignableUsersService.getAssignableUsersIncludingInactive(
      startWithEmpty,
      throwError
    );
  }

  getMyPortfolio(assignableUsers: UserList) {
    return assignableUsers.getByUserId(this.myId)?.portfolioId || '';
  }

  getCurrentUser(assignableUsers: UserList) {
    return assignableUsers.getByUserId(this.myId);
  }

  getOtherUsers(assignableUsers: UserList) {
    return assignableUsers.filter((u) => u.id !== this.myId);
  }

  get myId() {
    return this.claimsService.userId;
  }
}
